<template>
  <v-dialog v-model="showDialog" width="900" scrollable>
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs"/>
    </template>
    <v-card>
      <v-card-title>{{ $t('upload_locations') }}</v-card-title>
      <v-card-text>
        <!-- File Upload Section -->
        <div v-if="!uploadResults" class="d-flex align-center">
          <v-file-input
            v-model="uploadFile"
            :placeholder="$tc('select_a_file')"
            class="mr-1"
            accept=".csv"
            :error-messages="errors"/>
          <v-btn fab text :loading="isUploading" color="primary" @click="fileUpload()">
            <v-icon>mdi-upload</v-icon>
          </v-btn>
        </div>

        <!-- Results Section -->
        <div v-if="uploadResults">
          <!-- Summary -->
          <v-alert type="success" outlined class="mb-4">
            <div class="d-flex justify-space-around text-center">
              <div>
                <div class="text-h5 success--text">{{ uploadResults.summary.created }}</div>
                <div class="text-caption">{{ $t('created') }}</div>
              </div>
              <div>
                <div class="text-h5 info--text">{{ uploadResults.summary.assigned }}</div>
                <div class="text-caption">{{ $t('assigned') }}</div>
              </div>
              <div>
                <div class="text-h5 warning--text">{{ uploadResults.summary.skipped }}</div>
                <div class="text-caption">{{ $t('skipped') }}</div>
              </div>
              <div>
                <div class="text-h5 error--text">{{ uploadResults.summary.errors }}</div>
                <div class="text-caption">{{ $tc('error', 2) }}</div>
              </div>
            </div>
          </v-alert>

          <!-- Detailed Results Table -->
          <v-data-table
            :headers="resultsTableHeaders"
            :items="uploadResults.results"
            :items-per-page="10"
            class="elevation-1"
            dense>
            <template #item.action="{ item }">
              <v-chip :color="getActionColor(item.action)" small dark>
                <v-icon small left>{{ getActionIcon(item.action) }}</v-icon>
                {{ $t(item.action) }}
              </v-chip>
            </template>
            <template #item.coordinates="{ item }">
              <span v-if="item.latitude && item.longitude">
                {{ item.latitude }}, {{ item.longitude }}
              </span>
              <span v-else>-</span>
            </template>
            <template #item.message="{ item }">
              <span class="text-caption">{{ item.message || '-' }}</span>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn v-if="uploadResults" text color="primary" @click="resetDialog">
          {{ $t('upload_another') }}
        </v-btn>
        <v-btn text @click="closeDialog">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ValidationMixin from '@/components/mixins/ValidationMixin'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'UploadLocationDialog',
  mixins: [
    ValidationMixin
  ],
  data () {
    return {
      showDialog: false,
      uploadFile: null,
      isUploading: false,
      uploadResults: null
    }
  },
  validations: {
    uploadFile: {
      required
    }
  },
  computed: {
    errors () {
      return this.validationError({
        prop: this.$v.uploadFile,
        message: this.$t('required')
      })
    },
    resultsTableHeaders () {
      return [
        { text: this.$t('row'), value: 'row', width: '60px' },
        { text: this.$t('action'), value: 'action', width: '120px' },
        { text: this.$tc('name'), value: 'label' },
        { text: this.$tc('code'), value: 'code', width: '120px' },
        { text: this.$t('coordinates'), value: 'coordinates', width: '180px' },
        { text: this.$t('message'), value: 'message' }
      ]
    }
  },
  methods: {
    async fileUpload () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }
      this.isUploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.uploadFile)
        const response = await this.$httpAdapter({
          headers: { 'Content-Type': 'multipart/form-data' },
          url: this.$orm('Location').endpoint + '/upload',
          method: 'POST',
          data: formData
        })
        this.uploadResults = response.data
        this.$emit('upload-complete', this.uploadResults)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isUploading = false
      }
    },
    getActionColor (action) {
      const colors = {
        created: 'success',
        assigned: 'info',
        skipped: 'warning',
        error: 'error'
      }
      return colors[action] || 'grey'
    },
    getActionIcon (action) {
      const icons = {
        created: 'mdi-plus-circle',
        assigned: 'mdi-link',
        skipped: 'mdi-skip-next-circle',
        error: 'mdi-alert-circle'
      }
      return icons[action] || 'mdi-help-circle'
    },
    resetDialog () {
      this.uploadFile = null
      this.uploadResults = null
      this.$v.$reset()
    },
    closeDialog () {
      this.showDialog = false
      this.resetDialog()
    }
  }
}
</script>
